import { useCallback, useEffect, useMemo } from "react";
import { ICart, ICatalogOffer } from "@features/cart/ICart";
import { useOffers } from "@hooks/useOffers";
import { selectOfferByServiceGroup, selectOfferInCartByServiceGroup } from "@utils/offerUtils";
import { clearBroadbandEmailSent, getPrequal, getProspectReference, removeItemFromStorage } from "@utils/storage";
import { useHistory } from "react-router-dom";
import { EServiceGroup, ROUTES } from "@constants";
import { IEirSalesPrequal } from "@modules/eirSalesFacade/interfaces/models/IEirSalesPrequal";
import { useAddOffer } from "@hooks/useAddOffer";
import { useOfferAddons } from "@modules/eirSalesFacade/swr/offerAddons";
import { useDeleteOffer } from "@features/cart/hook/useDeleteOffer";

export const useHomeBroadbandResult = () => {
    const { offerList, offerListState, getOfferList, cart, cartState, getCart, setCart } = useOffers();
    const { addBroadbandOffer, addBroadbandOfferState } = useAddOffer();

    const { push } = useHistory();
    const storedPrequal = getPrequal();

    const goBack = useCallback(() => push(`..${ROUTES.BROADBAND_PAGE_URL}`), [push]);
    const goToLogin = useCallback(() => push(ROUTES.BROADBAND_LOGIN_PAGE_URL), [push]);

    const broadbandOffer = useMemo(() => selectOfferByServiceGroup(offerList, EServiceGroup.SA_BB), [offerList]);
    const broadbandOfferInCart = useMemo(() => selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB), [cart]);
    const hasBroadbandOfferInCart = useMemo(() => Boolean(broadbandOfferInCart), [broadbandOfferInCart]);

    const { data: offerAddonList } = useOfferAddons(broadbandOfferInCart?.offerId);

    useEffect(() => {
        if (!storedPrequal) {
            goBack();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goBack]);

    const { error: deleteOfferError, handleDeleteOffer, isLoading: deleteOfferIsLoading } = useDeleteOffer({ setCart });
    const isLoadingList = [offerListState.isLoading, cartState.isLoading, deleteOfferIsLoading];
    const isLoading = isLoadingList.some((loading) => loading);
    const errorOptions = [offerListState.error, cartState.error, deleteOfferError];
    const error = errorOptions.find((isError) => isError);

    const handleAddBroadbandOffer = useCallback(
        (selectedOffer: ICatalogOffer, prequal: IEirSalesPrequal) => {
            const prospectReference = getProspectReference();

            if (prospectReference && prequal.prequalId) {
                addBroadbandOffer(selectedOffer.catalogOfferCode, prequal.prequalId, prospectReference).then(
                    (newCart?: ICart) => {
                        if (newCart) {
                            setCart(newCart);
                        }
                    }
                );
            }
        },
        [addBroadbandOffer, setCart]
    );

    const resetStoredPrequal = useCallback(() => {
        removeItemFromStorage("prequal");
        clearBroadbandEmailSent();
        goBack();
    }, [goBack]);

    const handleChangeAddress = useCallback(() => {
        if (broadbandOfferInCart) {
            handleDeleteOffer(broadbandOfferInCart).then((newCart) => {
                if (newCart) {
                    resetStoredPrequal();
                }
            });
        } else {
            resetStoredPrequal();
        }
    }, [handleDeleteOffer, broadbandOfferInCart, resetStoredPrequal]);

    const getRetryFunction = useCallback(() => {
        if (offerListState.error) {
            return getOfferList;
        }
        if (cartState.error) {
            return getCart;
        }

        return () => window.location.reload();
    }, [cartState.error, offerListState.error, getCart, getOfferList]);

    return {
        storedPrequal,
        resetStoredPrequal,
        broadbandOffer,
        hasBroadbandOfferInCart,
        isLoading,
        error,
        handleAddBroadbandOffer,
        addBroadbandOfferState,
        retry: getRetryFunction(),
        goToLogin,
        handleChangeAddress,
        offerAddonList,
        broadbandCartOffer: broadbandOfferInCart,
    };
};
